import { generatePath } from 'react-router'
import { routes } from '~/queries/routes'

import { trackEvent } from '~/utils/tracking'
import { Link, useLoaderData } from '@remix-run/react'
import type { CareerAdvicePageDataType } from '~/routes/career-advice._index'
import classNames from 'classnames'
import { ResponsiveImage } from '~/components/ResponsiveImage'
import { TypeArticle, TypeCategorySortingSkeleton } from '~/types/contentful'
import { Asset, Entry } from 'contentful'

export type ArticlePreviewProps = {
  data: TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const ArticlePreview = ({ data }: ArticlePreviewProps) => {
  const { slug, title, abstract, category, color, illustration, illustrationAlt, gated } =
    data.fields
  const { page } = useLoaderData<CareerAdvicePageDataType>()

  const illustrationImg = illustration as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>
  const categoryAsset = category as unknown as Entry<
    TypeCategorySortingSkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >

  return (
    <Link
      to={generatePath(routes.article, { slug })}
      onClick={() =>
        trackEvent('click_content', {
          type: 'career-advice',
          gated: 'no',
          content_id: data.sys.id,
          page,
          category: categoryAsset.fields?.name || '',
        })
      }
    >
      <div
        className={classNames(
          'relative bg-neutral-5 text-neutral-100 rounded-[28px] h-full pt-5 px-6 pb-14',
        )}
        style={{ backgroundColor: color }}
      >
        {gated && (
          <div className="absolute top-4 right-4 py-1 px-3 bg-white text-neutral-100 rounded-[18px] text-s max-xl:px-2">
            Premium
          </div>
        )}
        {categoryAsset && <div className="text-s mb-5">{categoryAsset.fields.name}</div>}
        {illustrationImg && (
          <div className="block w-full max-w-[270px] mx-auto mb-4 min-h-[270px]">
            <ResponsiveImage
              asset={illustrationImg.fields}
              alt={illustrationAlt}
              opts={{ width: 270, quality: 95, load: 'lazy' }}
            />
          </div>
        )}
        <h3 className="text-m mb-4 block">{title}</h3>
        {abstract && (
          <p
            className="text-s overflow-hidden pr-13"
            style={{ display: '-webkit-box', WebkitLineClamp: '4', WebkitBoxOrient: 'vertical' }}
          >
            {abstract}
          </p>
        )}
      </div>
    </Link>
  )
}
